import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Loader2, ChevronLeft } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { api } from '@core/api/apiClient';
import BookingHeader from '@features/booking/components/BookingHeader';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';

type PageState = 'loading' | 'success' | 'error';

export function ConfirmEmailPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [pageState, setPageState] = useState<PageState>('loading');

  useEffect(() => {
    api.post('/confirm-email')
      .then(() => setPageState('success'))
      .catch(() => setPageState('error'));
  }, []);

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">
          {pageState === 'loading' && (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-brand mb-4" />
            </div>
          )}

          {pageState === 'success' && (
            <>
              <div className="flex flex-col items-center pt-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
                  <CheckCircle2 className="text-green-600" size={32} />
                </div>
                <h1 className="text-3xl font-display mb-2 text-center">
                  {t('CONFIRM_EMAIL_SUCCESS_TITLE')}
                </h1>
                <h2 className="text-xl font-display mb-4 text-center text-foreground/70">
                  {t('CONFIRM_EMAIL_SUCCESS_SUBTITLE')}
                </h2>
                <p className="text-foreground/50 text-sm mb-8 text-center max-w-xs">
                  {t('CONFIRM_EMAIL_SUCCESS_TEXT')}
                </p>
              </div>
              <div className="w-full max-w-sm px-4 space-y-3">
                <Button
                  className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
                  onClick={() => navigate('/profile')}
                >
                  {t('CONFIRM_EMAIL_MY_ACCOUNT')}
                </Button>
                <Button
                  variant="outline"
                  className="font-medium py-6 px-8 rounded-xl w-full"
                  onClick={() => navigate('/')}
                >
                  {t('CONFIRM_EMAIL_CLOSE')}
                </Button>
              </div>
            </>
          )}

          {pageState === 'error' && (
            <>
              <div className="flex flex-col items-center pt-4">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
                  <XCircle className="text-red-600" size={32} />
                </div>
                <h1 className="text-3xl font-display mb-2 text-center">
                  {t('CONFIRM_EMAIL_ERROR_TITLE')}
                </h1>
                <p className="text-foreground/50 text-sm mb-8 text-center max-w-xs">
                  {t('CONFIRM_EMAIL_ERROR_TEXT')}
                </p>
              </div>
              <div className="w-full max-w-sm px-4">
                <Button
                  className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
                  onClick={() => navigate('/')}
                >
                  {t('CONFIRM_EMAIL_CLOSE')}
                </Button>
              </div>
            </>
          )}
        </main>

        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
}

export default ConfirmEmailPage;
